import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

export interface ResolutionMetadata {
  resolution_summary?: string
  root_cause?: string
  resolution_type?: string
  followup_actions?: string
}

interface AlertCloseDialogProps {
  open: boolean
  onClose: () => void
  alert?: {
    id: string | number
    alertID: number
    summary: string
  }
  onSubmit: (metadata: ResolutionMetadata) => void | Promise<void>
  loading?: boolean
}

export default function AlertCloseDialog({
  open,
  onClose,
  alert,
  onSubmit,
  loading = false,
}: AlertCloseDialogProps): React.JSX.Element {
  const [formData, setFormData] = useState<ResolutionMetadata>({
    resolution_summary: '',
    root_cause: '',
    resolution_type: '',
    followup_actions: '',
  })
  const [errors, setErrors] = useState<{
    resolution_summary?: string
    root_cause?: string
  }>({})

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (
      (name === 'resolution_summary' && errors.resolution_summary) ||
      (name === 'root_cause' && errors.root_cause)
    ) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    const validationErrors: { resolution_summary?: string; root_cause?: string } = {}

    if (!formData.resolution_summary?.trim()) {
      validationErrors.resolution_summary = 'Resolution summary is required'
    }

    if (!formData.root_cause?.trim()) {
      validationErrors.root_cause = 'Root cause analysis is required'
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    // Filter out empty values
    const metadata: ResolutionMetadata = {}
    if (formData.resolution_summary?.trim()) {
      metadata.resolution_summary = formData.resolution_summary.trim()
    }
    if (formData.root_cause?.trim()) {
      metadata.root_cause = formData.root_cause.trim()
    }
    if (formData.resolution_type) {
      metadata.resolution_type = formData.resolution_type
    }
    if (formData.followup_actions?.trim()) {
      metadata.followup_actions = formData.followup_actions.trim()
    }

    await onSubmit(metadata)
    // Reset form
    setFormData({
      resolution_summary: '',
      root_cause: '',
      resolution_type: '',
      followup_actions: '',
    })
    setErrors({})
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({
        resolution_summary: '',
        root_cause: '',
        resolution_type: '',
        followup_actions: '',
      })
      setErrors({})
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Close Alert</DialogTitle>

      <DialogContent>
        {alert ? (
          <Typography variant="body2" color="textSecondary" sx={{ pb: 2 }}>
            Alert #{alert.alertID}: {alert.summary}
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ pb: 2 }}>
            Closing all open alerts for this service.
          </Typography>
        )}

        <form id="close-alert-form" onSubmit={handleSubmit}>
          {/* Resolution Summary - Required */}
          <TextField
            fullWidth
            required
            name="resolution_summary"
            label="Resolution Summary"
            placeholder="What action resolved the alert?"
            value={formData.resolution_summary}
            onChange={handleChange}
            error={!!errors.resolution_summary}
            helperText={
              errors.resolution_summary ||
              'Briefly describe how the alert was resolved'
            }
            multiline
            rows={2}
            sx={{ mb: 2 }}
            disabled={loading}
          />

          {/* Root Cause - Required */}
          <TextField
            fullWidth
            required
            name="root_cause"
            label="Root Cause (RCA)"
            placeholder="What was the underlying cause?"
            value={formData.root_cause}
            onChange={handleChange}
            error={!!errors.root_cause}
            helperText={
              errors.root_cause || 'Describe the root cause of this alert'
            }
            multiline
            rows={2}
            sx={{ mb: 2 }}
            disabled={loading}
          />

          {/* Resolution Type - Optional */}
          <TextField
            fullWidth
            select
            name="resolution_type"
            label="Resolution Type (Optional)"
            value={formData.resolution_type}
            onChange={handleChange}
            SelectProps={{ native: true }}
            sx={{ mb: 2 }}
            disabled={loading}
          >
            <option value=""></option>
            <option value="manual_fix">Manual fix</option>
            <option value="rollback">Rollback</option>
            <option value="auto_recovery">Auto-recovery</option>
            <option value="false_positive">False positive</option>
          </TextField>

          {/* Follow-up Actions - Optional */}
          <TextField
            fullWidth
            name="followup_actions"
            label="Follow-up Actions (Optional)"
            placeholder="Links to tickets, postmortem, or TODOs"
            value={formData.followup_actions}
            onChange={handleChange}
            multiline
            rows={2}
            disabled={loading}
          />
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="close-alert-form"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Closing...' : 'Close Alert'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
